/**
 * Presentation rules for the package-level quality gate's warnings.
 *
 * The gate emits developer-facing identifiers, e.g.
 *
 *   system_architecture.subsystems[Kontrol ve İletişim Alt Sistemi]: no component
 *   or material realises this communications subsystem, so the BOM cannot be
 *   called reconciled
 *
 * The panel used to print those verbatim, in English, truncated to the first
 * eight. For the Turkish-speaking operator the package is generated for, that is
 * not a disclosure — it is noise. These helpers turn each warning into a sentence
 * in the reader's language, grouped by the section it concerns.
 *
 * Two rules hold throughout:
 *
 *   - nothing is ever hidden. An unrecognised warning is passed through verbatim
 *     and marked untranslated, so a gate rule added later still reaches the user
 *     instead of vanishing from a panel whose whole purpose is disclosure.
 *   - nothing is ever invented. Translations only rephrase what the warning
 *     already says; no severity, cause or remedy is added.
 */

const SECTION_LABELS = {
  cost_analysis: { tr: 'Maliyet analizi', en: 'Cost analysis' },
  engineering_analysis: { tr: 'Mühendislik analizi', en: 'Engineering analysis' },
  system_architecture: { tr: 'Sistem mimarisi', en: 'System architecture' },
  market_analysis: { tr: 'Pazar analizi', en: 'Market analysis' },
  safety_case: { tr: 'Güvenlik dosyası', en: 'Safety case' },
  components: { tr: 'Bileşen listesi', en: 'Component list' },
  materials: { tr: 'Malzeme listesi', en: 'Material list' },
  verification_plan: { tr: 'Doğrulama planı', en: 'Verification plan' },
  prototype_roadmap: { tr: 'Prototip yol haritası', en: 'Prototype roadmap' },
  presentation_sections: { tr: 'Sunum bölümleri', en: 'Presentation sections' },
  technical_specifications: { tr: 'Teknik özellikler', en: 'Technical specifications' },
  manufacturing_plan: { tr: 'Üretim planı', en: 'Manufacturing plan' },
};

/**
 * Human labels for the field names the gate reports inside a section.
 *
 * Several rules used to end in `path.split('.').pop()`, which printed the
 * payload's own key — "prohibited_actions", "fail_safe_behaviors", "som". The
 * panel exists to disclose what the gate found to the operator the package was
 * generated for, and an identifier out of a JSON schema discloses nothing to
 * them.
 *
 * This map is deliberately limited to the fields the gate actually emits, read
 * off the required-field tuples and cost checks in server.py. Adding entries for
 * fields no rule reports would be inventing a vocabulary, and an unmapped field
 * still reaches the reader verbatim via `fieldLabel`.
 */
export const FIELD_LABELS = {
  // engineering_analysis
  load_or_performance_calculations: { tr: 'yük ve performans hesapları', en: 'load and performance calculations' },
  electrical_or_signal_architecture: { tr: 'elektrik ve sinyal mimarisi', en: 'electrical and signal architecture' },
  mechanical_layout: { tr: 'mekanik yerleşim', en: 'mechanical layout' },
  control_logic: { tr: 'kontrol mantığı', en: 'control logic' },
  fmea: { tr: 'FMEA (hata türü ve etkileri analizi)', en: 'FMEA (failure mode and effects analysis)' },
  cybersecurity: { tr: 'siber güvenlik', en: 'cybersecurity' },
  mass_balance: { tr: 'kütle dengesi', en: 'mass balance' },
  bom_mass_kg: { tr: 'BOM kütlesi (kg)', en: 'BOM mass (kg)' },
  // market_analysis
  tam: { tr: 'TAM (toplam ulaşılabilir pazar)', en: 'TAM (total addressable market)' },
  sam: { tr: 'SAM (hizmet verilebilir pazar)', en: 'SAM (serviceable addressable market)' },
  som: { tr: 'SOM (elde edilebilir pazar payı)', en: 'SOM (serviceable obtainable market)' },
  pricing_strategy: { tr: 'fiyatlandırma stratejisi', en: 'pricing strategy' },
  business_model: { tr: 'iş modeli', en: 'business model' },
  unit_economics: { tr: 'birim ekonomisi', en: 'unit economics' },
  go_to_market: { tr: 'pazara giriş planı', en: 'go-to-market plan' },
  adoption_risks: { tr: 'benimsenme riskleri', en: 'adoption risks' },
  defensibility: { tr: 'savunulabilirlik', en: 'defensibility' },
  evidence_status: { tr: 'kanıt durumu', en: 'evidence status' },
  evidence_needed: { tr: 'gereken kanıt', en: 'evidence needed' },
  // system_architecture
  mobility: { tr: 'hareket kabiliyeti', en: 'mobility' },
  sensor_fusion: { tr: 'sensör füzyonu', en: 'sensor fusion' },
  operator_controls: { tr: 'operatör kumandaları', en: 'operator controls' },
  power_system: { tr: 'güç sistemi', en: 'power system' },
  communications: { tr: 'haberleşme', en: 'communications' },
  safe_marking: { tr: 'güvenlik işaretlemesi', en: 'safety marking' },
  fail_safe_behaviors: { tr: 'arıza-emniyet davranışları', en: 'fail-safe behaviours' },
  // safety_case
  automatic_stop_conditions: { tr: 'otomatik durdurma koşulları', en: 'automatic stop conditions' },
  hazards: { tr: 'tehlikeler', en: 'hazards' },
  prohibited_actions: { tr: 'yasaklı işlemler', en: 'prohibited actions' },
  // cost_analysis
  materials_cost: { tr: 'malzeme maliyeti', en: 'materials cost' },
  labor_cost: { tr: 'işçilik maliyeti', en: 'labour cost' },
  tooling_cost: { tr: 'kalıp ve takım maliyeti', en: 'tooling cost' },
  overhead_cost: { tr: 'genel üretim gideri', en: 'overhead cost' },
  low_estimate: { tr: 'alt maliyet tahmini', en: 'low cost estimate' },
  high_estimate: { tr: 'üst maliyet tahmini', en: 'high cost estimate' },
  total_cost: { tr: 'toplam birim maliyet', en: 'total unit cost' },
  confidence: { tr: 'güven düzeyi', en: 'confidence level' },
  currency: { tr: 'para birimi', en: 'currency' },
  estimate_range: { tr: 'tahmin aralığı', en: 'estimate range' },
  materials_cost_basis: { tr: 'malzeme maliyeti dayanağı', en: 'materials cost basis' },
  price_basis_date: { tr: 'fiyat baz tarihi', en: 'price basis date' },
  // technical_specifications
  dimensions: { tr: 'ölçüler', en: 'dimensions' },
};

/** Subsystem concepts the architecture-coverage rule names in English. */
const CONCEPT_LABELS = {
  power: { tr: 'güç', en: 'power' },
  communications: { tr: 'haberleşme', en: 'communications' },
  sensing: { tr: 'algılama', en: 'sensing' },
  control: { tr: 'kontrol', en: 'control' },
  actuation: { tr: 'eyleyici', en: 'actuation' },
  enclosure: { tr: 'muhafaza', en: 'enclosure' },
};

function lang(language) {
  return language === 'tr' ? 'tr' : 'en';
}

/** Human label for a top-level package section, or the raw key when unknown. */
export function sectionLabel(section, language) {
  const entry = SECTION_LABELS[section];
  return entry ? entry[lang(language)] : section;
}

/**
 * Human label for the field a warning points at, or the raw key when unknown.
 *
 * Falling back to the key keeps the module's promise that nothing is hidden: a
 * field a later gate rule reports still reaches the reader, just untranslated.
 */
export function fieldLabel(path, language) {
  const last = String(path == null ? '' : path).split('.').pop().replace(/\[.*\]$/, '');
  const entry = FIELD_LABELS[last];
  return entry ? entry[lang(language)] : last;
}

/**
 * Split a warning into its section, the bracketed label it points at, and the
 * English explanation that follows the colon.
 */
export function parseWarning(raw) {
  const text = String(raw == null ? '' : raw).trim();
  const separator = text.indexOf(': ');
  const path = separator === -1 ? text : text.slice(0, separator);
  const detail = separator === -1 ? '' : text.slice(separator + 2);
  const bracket = path.match(/\[(.+)\]/);
  return {
    raw: text,
    path,
    section: path.split(/[.[]/)[0] || '',
    label: bracket ? bracket[1] : null,
    detail,
  };
}

function translateConcepts(concepts, language) {
  return concepts
    .split('/')
    .map((concept) => {
      const entry = CONCEPT_LABELS[concept.trim()];
      return entry ? entry[lang(language)] : concept.trim();
    })
    .join(' / ');
}

/*
 * Each rule maps one gate message to a sentence. Ordered, first match wins, so
 * the specific patterns precede the generic "<field>: required" ones.
 */
const RULES = [
  {
    match: /^requires at least (\d+) entries, got (\d+)$/,
    tr: (m, w) => `${sectionLabel(w.section, 'tr')} en az ${m[1]} madde içermeli; bu paket ${m[2]} madde içeriyor.`,
    en: (m, w) => `${sectionLabel(w.section, 'en')} needs at least ${m[1]} entries; this package has ${m[2]}.`,
  },
  {
    // The safety-case rules state a minimum without reporting the actual count.
    match: /^requires at least (\d+) entries$/,
    tr: (m, w) => `${sectionLabel(w.section, 'tr')} içinde “${fieldLabel(w.path, 'tr')}” en az ${m[1]} madde gerektiriyor.`,
    en: (m, w) => `${sectionLabel(w.section, 'en')} needs at least ${m[1]} entries under “${fieldLabel(w.path, 'en')}”.`,
  },
  {
    match: /^no component or material realises this (.+) subsystem/,
    tr: (m, w) => `“${w.label}” alt sistemini karşılayan hiçbir bileşen veya malzeme yok (${translateConcepts(m[1], 'tr')}), bu yüzden BOM uzlaştırılmış sayılamaz.`,
    en: (m, w) => `No component or material realises the “${w.label}” subsystem (${translateConcepts(m[1], 'en')}), so the BOM cannot be called reconciled.`,
  },
  {
    match: /^every input is numeric, so the result must be evaluated rather than left TBD$/,
    tr: (m, w) => `“${w.label}” hesabının bütün girdileri sayısal, buna rağmen sonuç TBD bırakılmış.`,
    en: (m, w) => `Every input to the “${w.label}” calculation is numeric, yet its result was left TBD.`,
  },
  {
    match: /^states a quantified result while these inputs remain TBD \((.+)\)$/,
    tr: (m, w) => `“${w.label}” hesabı sayısal bir sonuç veriyor ama şu girdileri TBD: ${m[1]}.`,
    en: (m, w) => `The “${w.label}” calculation states a quantified result while these inputs remain TBD: ${m[1]}.`,
  },
  {
    match: /^explicit currency code is required$/,
    tr: () => 'Para birimi belirtilmemiş, bu yüzden maliyet rakamları birimsiz.',
    en: () => 'No currency code was stated, so the cost figures carry no unit.',
  },
  {
    match: /^low\/high must include reconciled total$/,
    tr: () => 'Maliyet aralığının alt ve üst sınırı, uzlaştırılmış toplamı kapsamıyor.',
    en: () => 'The cost estimate range does not contain the reconciled total.',
  },
  {
    match: /^reconciled positive total required$/,
    tr: () => 'BOM ile uzlaştırılmış, pozitif bir toplam maliyet üretilmemiş.',
    en: () => 'No positive total cost reconciled against the BOM was produced.',
  },
  {
    match: /^must be greater than or equal to low_estimate$/,
    tr: () => 'Üst maliyet tahmini, alt tahminden küçük.',
    en: () => 'The high cost estimate is below the low estimate.',
  },
  {
    match: /^positive numeric estimate required$/,
    tr: (m, w) => `Maliyet kalemi “${fieldLabel(w.path, 'tr')}” için pozitif sayısal bir tahmin gerekiyor.`,
    en: (m, w) => `Cost field “${fieldLabel(w.path, 'en')}” needs a positive numeric estimate.`,
  },
  {
    match: /^low, medium or high is required$/,
    tr: () => 'Maliyet tahmininin güven düzeyi (düşük / orta / yüksek) belirtilmemiş.',
    en: () => 'The cost estimate states no confidence level (low / medium / high).',
  },
  {
    match: /^date or TBD is required$/,
    tr: () => 'Fiyatların hangi tarihe dayandığı belirtilmemiş.',
    en: () => 'No price basis date was stated.',
  },
  {
    match: /^every material needs an explicit line_cost$/,
    tr: () => 'Bazı malzemelerin satır maliyeti yok, bu yüzden malzeme maliyeti BOM ile doğrulanamıyor.',
    en: () => 'Some materials carry no line cost, so the material cost cannot be checked against the BOM.',
  },
  {
    match: /^explicit product-mass target is not reconciled$/,
    tr: () => 'Belirtilen ürün kütlesi hedefi, BOM kütlesiyle uzlaştırılmamış.',
    en: () => 'The stated product-mass target is not reconciled with the BOM mass.',
  },
  {
    match: /^numeric reconciled value required$/,
    tr: () => 'BOM kütlesi sayısal bir değer olarak hesaplanmamış.',
    en: () => 'The BOM mass was not computed as a numeric value.',
  },
  {
    match: /^requires at least (\d+) sensors$/,
    tr: (m) => `Sensör füzyonu en az ${m[1]} sensör gerektiriyor.`,
    en: (m) => `Sensor fusion requires at least ${m[1]} sensors.`,
  },
  {
    match: /^requires at least (\d+) hazards$/,
    tr: (m) => `Güvenlik dosyası en az ${m[1]} tehlike kaydı gerektiriyor.`,
    en: (m) => `The safety case requires at least ${m[1]} hazards.`,
  },
  {
    match: /^object is required for robot projects$/,
    tr: (m, w) => `${sectionLabel(w.section, 'tr')} robot projeleri için zorunlu, ancak üretilmemiş.`,
    en: (m, w) => `${sectionLabel(w.section, 'en')} is required for robot projects but was not produced.`,
  },
  {
    // Emitted by the specifications gate; mirrors the DXF export's own contract.
    match: /^width and depth \(or length\) are required for 2D\/DXF export$/,
    tr: () => 'Ölçüler arasında genişlik ve derinlik (veya uzunluk) yok, bu yüzden 2B/DXF çizim üretilemiyor.',
    en: () => 'The dimensions state no width and depth (or length), so no 2D/DXF drawing can be produced.',
  },
  {
    match: /^(complete object|object) is required$/,
    tr: (m, w) => `${sectionLabel(w.section, 'tr')} bölümü eksiksiz üretilmemiş.`,
    en: (m, w) => `${sectionLabel(w.section, 'en')} was not produced in full.`,
  },
  {
    match: /^required$/,
    tr: (m, w) => `${sectionLabel(w.section, 'tr')} içinde “${fieldLabel(w.path, 'tr')}” alanı eksik.`,
    en: (m, w) => `${sectionLabel(w.section, 'en')} is missing the “${fieldLabel(w.path, 'en')}” field.`,
  },
];

/**
 * A warning as the reader should see it.
 *
 * `translated` is false when no rule matched; `text` is then the gate's own
 * wording, shown verbatim rather than dropped or paraphrased into a guess.
 */
export function describeWarning(raw, language) {
  const parsed = parseWarning(raw);
  if (!parsed.raw) return null;
  for (const rule of RULES) {
    const matched = parsed.detail.match(rule.match);
    if (matched) {
      return {
        ...parsed,
        text: rule[lang(language)](matched, parsed),
        translated: true,
      };
    }
  }
  return { ...parsed, text: parsed.raw, translated: false };
}

/**
 * Warnings grouped by the section they concern, preserving gate order both
 * between groups and inside them.
 */
export function groupWarnings(warnings, language) {
  if (!Array.isArray(warnings)) return [];
  const groups = [];
  const bySection = new Map();
  warnings.forEach((warning) => {
    const described = describeWarning(warning, language);
    if (!described) return;
    let group = bySection.get(described.section);
    if (!group) {
      group = { section: described.section, label: sectionLabel(described.section, language), items: [] };
      bySection.set(described.section, group);
      groups.push(group);
    }
    group.items.push(described);
  });
  return groups;
}

/** One line stating how much the gate flagged, for the panel header. */
export function warningSummary(warnings, language) {
  const groups = groupWarnings(warnings, language);
  const items = groups.reduce((total, group) => total + group.items.length, 0);
  if (!items) return null;
  return lang(language) === 'tr'
    ? `${items} madde · ${groups.length} bölüm`
    : `${items} item${items === 1 ? '' : 's'} · ${groups.length} section${groups.length === 1 ? '' : 's'}`;
}
